import type { DateValue } from '@core/util/date';
import type { SubType } from '@entity';
import type { Property } from '@property/types';
import type { ChannelType } from '@service-cognition/generated/schemas/channelType';
import type { ItemType } from '@service-storage/client';
import type { CalendarMentionEvent } from '@service-storage/generated/schemas/calendarMentionEvent';
import type { FileType } from '@service-storage/generated/schemas/fileType';

type AccessType = 'access' | 'no_access' | 'does_not_exist';

type PreviewItemLoading = { loading: true } & BasePreviewItem;

export type PreviewItemNoAccess = {
  access: Extract<AccessType, 'no_access' | 'does_not_exist'>;
  loading: false;
} & BasePreviewItem;

type BasePreviewItem<T extends ItemType = ItemType> = {
  id: string;
  type: T;
  owner?: string;
  updatedAt?: DateValue | null;
};

/** this is a catch-all type for access items that do not have a more specific type */
type PreviewItemAccess = {
  access: Extract<AccessType, 'access'>;
  loading: false;
  rawName: string;
  name: string;
  fileType?: FileType;
  subType?: SubType;
  channelType?: never;
} & BasePreviewItem<
  Exclude<ItemType, 'project' | 'document' | 'channel' | 'calendar_event'>
>;

type PreviewProjectAccess = {
  access: Extract<AccessType, 'access'>;
  loading: false;
  rawName: string;
  name: string;
  fileType?: never;
  subType?: never;
  channelType?: never;
} & BasePreviewItem<'project'>;

/** Properties and viewer permission loaded by the same operation as a preview. */
export type PreviewDocumentMetadata = {
  properties: Property[];
  canEdit: boolean;
};

/** Undefined properties mean the rich preview is pending, not a REST fallback. */
export type PreviewDocumentProperties = {
  properties: Property[] | undefined;
  canEdit: boolean;
  refetch: () => Promise<void>;
};

type PreviewDocumentAccess = {
  access: Extract<AccessType, 'access'>;
  loading: false;
  rawName: string;
  name: string;
  fileType?: FileType;
  subType?: SubType;
  channelType?: never;
  /** Absent on lightweight cache seeds and REST previews. */
  documentMetadata?: PreviewDocumentMetadata;
} & BasePreviewItem<'document'>;

export type MessageContext = import('@service-storage/messages').Message;

export type PreviewChannelAccess = {
  access: Extract<AccessType, 'access'>;
  loading: false;
  rawName: string;
  name: string;
  fileType?: never;
  subType?: never;
  channelType?: ChannelType;
  profilePictureId?: string | null;
  messageContext?: MessageContext | undefined;
} & BasePreviewItem<'channel'>;

export type PreviewCalendarEventAccess = {
  access: Extract<AccessType, 'access'>;
  loading: false;
  rawName: string;
  name: string;
  fileType?: never;
  subType?: never;
  channelType?: never;
  /** The requester-relative meeting preview served by the calendar API. */
  event: CalendarMentionEvent;
} & BasePreviewItem<'calendar_event'>;

export type AccessiblePreviewItem =
  | PreviewItemAccess
  | PreviewProjectAccess
  | PreviewDocumentAccess
  | PreviewChannelAccess
  | PreviewCalendarEventAccess;

export type PreviewItem =
  | PreviewItemLoading
  | PreviewItemNoAccess
  | AccessiblePreviewItem;

type BaseItemEntity = {
  id: string;
  type?: Exclude<ItemType, 'channel'>;
};

type ChannelItemEntity = {
  id: string;
  type: 'channel';
  messageId?: string;
};

export type ItemEntity = BaseItemEntity | ChannelItemEntity;

export const isAccessiblePreviewItem = (
  item: PreviewItem
): item is AccessiblePreviewItem => {
  return !item.loading && item.access === 'access';
};

export const isPreviewItemNoAccess = (
  item: PreviewItem
): item is PreviewItemNoAccess => {
  return (
    !item.loading &&
    (item.access === 'no_access' || item.access === 'does_not_exist')
  );
};

export const isChannelPreviewItem = (
  item: PreviewItem
): item is PreviewChannelAccess => {
  return isAccessiblePreviewItem(item) && item.type === 'channel';
};

export const isCalendarEventPreviewItem = (
  item: PreviewItem
): item is PreviewCalendarEventAccess => {
  return isAccessiblePreviewItem(item) && item.type === 'calendar_event';
};
